export function lazyLoad(offset: string = "50%") {
  const imgList = $("img[yige-src]");
  const backgroundList = $("*[yige-background]");
  let observer: IntersectionObserver | null = null;

  // 定义加载逻辑
  const loading: IntersectionObserverCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = $(entry.target);
        const SRC = el.attr("yige-src") as string | undefined;
        const BACKGROUND = el.attr("yige-background");

        if (SRC) {
          el.attr("src", SRC);
          el.removeAttr("yige-src");
        }

        if (BACKGROUND) {
          el.css("background-image", `url(${BACKGROUND})`);
          el.removeAttr("yige-background");
        }

        observer.unobserve(entry.target);
      }
    });
  };

  // 初始化 IntersectionObserver
  if ("IntersectionObserver" in window) {
    observer = new IntersectionObserver(loading, {
      root: null,
      rootMargin: `0px 0px ${offset} 0px`,
      threshold: 0,
    });

    // 观察初始元素
    imgList.each((_, el) => observer?.observe(el));
    backgroundList.each((_, el) => observer?.observe(el));
  } else {
    // 不支持 IntersectionObserver 的情况下，立即加载所有元素
    imgList.each((_, img) => {
      const SRC = $(img).attr("yige-src");
      if (SRC) {
        $(img).attr("src", SRC).removeAttr("yige-src");
      }
    });

    backgroundList.each((_, background) => {
      const BACKGROUND = $(background).attr("yige-background");
      if (BACKGROUND) {
        $(background).css("background-image", `url(${BACKGROUND})`).removeAttr("yige-background");
      }
    });
  }

  // 定义 update 方法
  const update = () => {
    // 如果有 IntersectionObserver，重新收集并监听
    if (observer) {
      // 断开所有正在监听的元素
      observer.disconnect();

      // 重新收集元素
      const newImgList = $("img[yige-src]");
      const newBackgroundList = $("*[yige-background]");
      // 重新监听
      newImgList.each((_, el) => observer?.observe(el));
      newBackgroundList.each((_, el) => observer?.observe(el));
    } else {
      // 不支持 IntersectionObserver 的情况下，重新立即加载
      const newImgList = $("img[yige-src]");
      newImgList.each((_, img) => {
        const SRC = $(img).attr("yige-src");
        if (SRC) {
          $(img).attr("src", SRC).removeAttr("yige-src");
        }
      });

      const newBackgroundList = $("*[yige-background]");
      newBackgroundList.each((_, background) => {
        const BACKGROUND = $(background).attr("yige-background");
        if (BACKGROUND) {
          $(background).css("background-image", `url(${BACKGROUND})`).removeAttr("yige-background");
        }
      });
    }
  };

  // 返回 update 方法
  return { update };
}
export function resetImgSize({ nodeList, setImg = true, setBox = false, cb }: ResetImgSizeFn) {
  const temp: any[] = [];
  nodeList.each((_i, img) => {
    if (!$(img).is("img")) return;
    function resizeImage(image: HTMLImageElement) {
      const originalH = image.naturalHeight / 10 + "rem";
      const originalW = image.naturalWidth / 10 + "rem";
      if (setBox) {
        $(image).parents(".img-box").eq(0).css({
          height: originalH,
          width: originalW,
        });
      }
      if (setImg) {
        $(image).css({
          height: originalH,
          width: originalW,
        });
      }
      temp.push({
        item: img,
        w: originalW,
        h: originalH,
      });
      if (cb && typeof cb === "function") cb();
    }
    if ((img as HTMLImageElement).complete && (img as HTMLImageElement).src) {
      // 如果图片已加载
      resizeImage(img as HTMLImageElement);
    } else {
      $(img).on("load", function () {
        resizeImage(this as HTMLImageElement);
      });
    }
  });
  return temp;
}
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 200,
  options?: {
    leading?: boolean;
    trailing?: boolean;
  }
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastInvokeTime: number | null = null;
  // 保存最后的上下文，后面用于调用 func
  let lastContext: any;

  const { leading = true, trailing = true } = options || {};

  // 改进：将 invokeFunc 改为接受 context 和 args 参数
  const invokeFunc = (time: number, context: any, args: Parameters<T>) => {
    lastInvokeTime = time;
    func.apply(context, args);
    lastArgs = null;
    lastContext = null;
  };

  const timerExpired = () => {
    const now = Date.now();
    if (lastArgs && trailing) {
      // 注意这里用 lastContext，而非 this，因为 timerExpired 没有绑定正确的上下文
      invokeFunc(now, lastContext, lastArgs);
    }
    timeout = null;
  };

  return function throttled(this: any, ...args: Parameters<T>) {
    const now = Date.now();
    // 如果没有设置过上次执行时间，并且 leading 为 false，则初始化 lastInvokeTime
    if (lastInvokeTime === null && !leading) {
      lastInvokeTime = now;
    }
    const remaining = wait - (now - (lastInvokeTime || 0));
    // 保存当前调用的参数和上下文
    lastArgs = args;
    /* 
      ESLint 警告“Unexpected aliasing of 'this' to local variable”，
      在这种场景下我们需要捕获 this 来保证 func 的调用上下文，
      因此在此行禁用该规则：
    */
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastContext = this;

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      invokeFunc(now, lastContext, lastArgs);
    } else if (!timeout && trailing) {
      timeout = setTimeout(timerExpired, remaining);
    }
  };
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number = 200,
  options?: {
    immediate?: boolean;
  }
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const { immediate = false } = options || {};

  return function debounced(this: any, ...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(this, args);
    }
  };
}
export function handleSendInquiry({ formId, trigger, opts }: SendInquiryOpts) {
  const formEl = $(formId)[0] as HTMLFormElement;
  $(trigger).on("click", () => {
    const triggerElement = $(formEl).data("triggerElement");
    const formData = new FormData(formEl);
    const readyToSend = beforeSubmit(formData);
    if (readyToSend) {
      if (opts && opts.onBeforeSend && typeof opts.onBeforeSend === "function") {
        opts.onBeforeSend(formData);
      }
      sendAjax(formData, triggerElement);
    }
  });
  const beforeSubmit = (data: FormData) => {
    let FLAG = true;
    const disableFields: any = {};
    const formElements = formEl.elements;
    for (let i = 0; i < formElements.length; i++) {
      const element = formElements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const key = element.name;
      const type = element.type;
      const is_required = element.required;
      if (type === "file" && (data.get(key)! as File).name === "") {
        data.delete(key);
      }
      if (type === "checkbox" && !data.get(key)) {
        if (is_required) {
          FLAG = false;
          rejectMessage(key);
          break;
        } else {
          data.delete(key);
        }
      }
      if (element.disabled && element.value) {
        data.set(key, element.value);
      }
      if (is_required && element.value === "") {
        FLAG = false;
        let msg: string | undefined = undefined;
        if (opts && opts.inquiryMsg && opts.inquiryMsg[key]) {
          msg = opts.inquiryMsg[key];
        }
        rejectMessage(key, msg);
        break;
      }
    }
    /*! custom Setting Start */
    if (opts && opts.fn && typeof opts.fn === "function") {
      const res = opts.fn({ data, disableFields, rejectMessage });
      if (typeof res === "boolean") return res;
    }
    return FLAG;
  };
  const rejectMessage = (key: string, msg?: string) => {
    formEl[key].focus();
    alert(msg ?? `${key} can not be empty!`);
  };
  const sendAjax = (data: FormData, originalTrigger: HTMLElement) => {
    $.ajax({
      processData: false,
      contentType: false,
      type: "POST",
      url: "/inquiry",
      data: data,
      success: function (jsonResult) {
        if (jsonResult.code == 200) {
          ($(formEl)[0] as HTMLFormElement).reset();
          if (opts && opts.onSuccess && typeof opts.onSuccess === "function") {
            opts.onSuccess(jsonResult, originalTrigger);
          } else {
            location.href = "/page/thanks.html";
          }
        } else {
          if (opts && opts.onReject && typeof opts.onReject === "function") {
            opts.onReject(jsonResult, originalTrigger);
          } else {
            alert(jsonResult.msg);
          }
        }
        $(":input[name = '__token__']").val(jsonResult.data.token);
      },
      error: function (data) {
        if (opts && opts.onReject && typeof opts.onReject === "function") {
          opts.onReject(data, originalTrigger);
        }
      },
    });
  };
}
export function INIT({ h, searchTriggle, headerSelect = "header" }: { h?: number; searchTriggle?: string; headerSelect?: string }) {
  const isMobile = $(window).width()! <= 1024;
  const header = $(headerSelect);
  const scrollTopButton = $(".scroll-top");
  const searchBox = $(".search-box");
  const initHeaderSticky = (height: number): void => {
    $(window).on("scroll", () => {
      if ($(window).scrollTop()! > height) {
        header.addClass("fixeds");
        scrollTopButton.show(400);
      } else {
        header.removeClass("fixeds");
        scrollTopButton.hide(400);
      }
    });
  };
  const initSearch = (): void => {
    if (!searchBox.length || !searchTriggle) return;
    $(`header ${searchTriggle}`).on("click", (event) => {
      searchBox.slideToggle(500);
      $(document).one("click", () => {
        searchBox.slideUp(500);
      });
      event.stopPropagation();
    });
    searchBox.on("click", (event) => {
      event.stopPropagation();
    });
  };
  const initMobileMenu = (): void => {
    $(".header .trigger").on("click", function () {
      $(this).toggleClass("trigger_active");
      $(this).siblings(".content_mt_ul").slideToggle(300);
    });
    $(".header .mobile_nav .nav_bg").on("click", function () {
      $("html").css("overflow-y", "unset");
      $(".mobile_nav .mobile_nav_content").removeClass("nav_show");
    });
    $(".header .mobile_nav .cus-bars").on("click", function () {
      $("html").css("overflow-y", "hidden");
      $(".mobile_nav .mobile_nav_content").addClass("nav_show");
    });
  };
  scrollTopButton.on("click", () => {
    $(window).scrollTop(0);
  });
  if (!isMobile && h && typeof h === "number") {
    initHeaderSticky(h);
  }
  if (!isMobile && searchTriggle && typeof searchTriggle === "string") {
    initSearch();
  }
  if (isMobile) {
    initMobileMenu();
  }
}
export function ImportBundle(options: BundleImporter.BundleOptions, src: BundleImporter.ScriptSrc[] | string[]) {
  const PATH = window.location.pathname;
  const INDEX_FLAG = /index.html$/g.test(PATH);
  if (!options.isIndex && !options.onlyIndex && (PATH == "/" || INDEX_FLAG)) return;
  if (options.onlyIndex && PATH !== "/" && !INDEX_FLAG) return;
  if (options.isMobile && document.body.clientWidth < 768) return;

  let _count: number = src.length || 0;
  let _scritpLoaded: number = 0;
  let _resolve: (value: boolean) => void = () => {};
  const documentFragment: DocumentFragment = document.createDocumentFragment();

  // 生成css标签
  function createCssLink(linkSrc: string[]) {
    linkSrc.forEach((link) => {
      if (typeof link !== "string") return;
      const L = document.createElement("link");
      L.rel = "stylesheet";
      L.href = link;
      documentFragment.appendChild(L);
      _scritpLoaded++;
      checkAllScriptsLoaded();
    });
  }
  // 生成script标签
  function createScriptSrc(linkSrc: BundleImporter.ScriptSrc[]) {
    const onload = (item: BundleImporter.ScriptSrc) => {
      _scritpLoaded++;
      if (item.fn && typeof item.fn == "function") {
        item.fn();
      }
      checkAllScriptsLoaded();
    };

    linkSrc.forEach((item) => {
      if (!item || (!item.src && typeof item !== "object")) return;
      if (item.isMobile && document.body.clientWidth < 768) {
        _count--;
        return;
      }
      const script = document.createElement("script");
      script.src = item.src;
      script.defer = item?.defer ?? false;
      script.async = item?.async ?? false;
      script.onload = () => onload(item);
      documentFragment.appendChild(script);
    });
  }
  function checkAllScriptsLoaded() {
    if (_scritpLoaded === _count) {
      if (options.callback && typeof options.callback == "function") {
        options.callback();
      }
      _resolve(true);
    }
  }
  // 导入
  function Import() {
    return new Promise<boolean>((resolve, _reject) => {
      _resolve = resolve;
      const container = document.getElementsByTagName("head")[0];
      const Pos = container.querySelector('link[rel="stylesheet"]');
      if (options.type === "css") {
        createCssLink(src as string[]);
      } else {
        createScriptSrc(src as BundleImporter.ScriptSrc[]);
      }
      if (options.position && options.position === "end") {
        container.appendChild(documentFragment);
      } else {
        container.insertBefore(documentFragment, Pos);
      }
    });
  }
  return Import();
}
export function isMobile(w: number = 620): boolean {
  const windowWidth = $(window).width()!;
  return windowWidth <= w;
}
const preventDefault = (e: Event) => {
  e.preventDefault();
};
export function catchWheelEvent(enable: boolean) {
  if (enable) {
    $("html").css("overflow-y", "hidden");
    document.addEventListener("wheel", preventDefault, { passive: false });
    document.addEventListener("touchmove", preventDefault, { passive: false });
  } else {
    $("html").css("overflow-y", "initial");
    document.removeEventListener("wheel", preventDefault);
    document.removeEventListener("touchmove", preventDefault);
  }
}
/**
 * 依赖 "--item" css变量
 * @param {JQuery<HTMLElement>} list
 */
export function listToGroup<T extends JQuery>(list: T) {
  const breakpoint = Number(list.css("--item"));
  const tempList: T[] = [];
  for (let i = 0; i < list.length; i += breakpoint) {
    const group = list.slice(i, i + breakpoint);
    tempList.push(group);
  }
  return tempList;
}
